import React, { useEffect, useMemo, useState } from "react";

type TestItem = {
  id: number;
  title: string;
  topic_name?: string;
  course_name: string;
  assigned_users: string;
  question_count: number;
  pass_mark?: number;
  total_marks?: number;
  start_date?: string;
  end_date?: string;
  status: string;
  questions_json?: string;
};

type TestQuestionItem = {
  questionText?: string;
  question_text?: string;
  marks?: number;
  options?: string[];
  correctAnswers?: (number | string)[];
  correct_answers?: (number | string)[];
  correctAnswerIndexes?: (number | string)[];
  correct_answer_indexes?: (number | string)[];
  correctOptionIndexes?: (number | string)[];
  correct_option_indexes?: (number | string)[];
};

type CompletedTestResult = {
  testId: string;
  score: number;
  passed: boolean;
  selectedAnswers: Record<number, number[]>;
};


type LoggedInUser = {
  id?: number;
  username?: string;
  email?: string;
  role?: string;
  department?: string;
};

type Props = {
  employeeTests: TestItem[];
  autoOpenTestId?: number | null;
  onAutoOpenHandled?: () => void;
};

const COMPLETED_TESTS_KEY = "employeeCompletedTests";

function EmployeeTestPage({
  employeeTests,
  autoOpenTestId,
  onAutoOpenHandled,
}: Props) {

  const [selectedTest, setSelectedTest] = useState<TestItem | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number[]>>({});
  const [testResult, setTestResult] = useState<{ score: number; passed: boolean } | null>(
    null
  );

  const [completedRefreshKey, setCompletedRefreshKey] = useState(0);

  const completedTests = useMemo<CompletedTestResult[]>(() => {
    const stored = localStorage.getItem(COMPLETED_TESTS_KEY);

    if (!stored) {
      return [];
    }

    try {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [completedRefreshKey]);

  const selectedQuestions = useMemo<TestQuestionItem[]>(() => {
    if (!selectedTest?.questions_json) {
      return [];
    }

    try {
      const parsed = JSON.parse(selectedTest.questions_json);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [selectedTest]);

  const getTestStorageKey = (test: TestItem) => {
    return `${test.id}-${test.title}-${test.course_name}-${test.start_date}-${test.end_date}`;
  };

  const getCompletedResult = (test: TestItem) => {
    return completedTests.find((item) => item.testId === getTestStorageKey(test));
  };

  const handleStartTest = (test: TestItem) => {
    const completedResult = getCompletedResult(test);

    setSelectedTest(test);
    setSelectedAnswers(completedResult?.selectedAnswers || {});
    setTestResult(
      completedResult
        ? {
            score: completedResult.score,
            passed: completedResult.passed,
          }
        : null
    );
  };



  useEffect(() => {
  const targetCourseName = localStorage.getItem("employeeAutoOpenTestCourseName");

  if (!targetCourseName || selectedTest) {
    return;
  }

  const targetTest = employeeTests.find(
    (test) =>
      test.course_name.trim().toLowerCase() ===
      targetCourseName.trim().toLowerCase()
  );

  if (targetTest) {
    handleStartTest(targetTest);
  }

  localStorage.removeItem("employeeAutoOpenTestCourseName");
}, [employeeTests, selectedTest]);


useEffect(() => {
  if (!autoOpenTestId || selectedTest) {
    return;
  }

  const targetTest = employeeTests.find((test) => test.id === autoOpenTestId);

  if (targetTest) {
    handleStartTest(targetTest);
  }

  onAutoOpenHandled?.();
}, [autoOpenTestId, employeeTests, selectedTest, onAutoOpenHandled]);





  const handleBackToList = () => {
    setSelectedTest(null);
    setSelectedAnswers({});
    setTestResult(null);
  };

  const handleToggleAnswer = (questionIndex: number, optionIndex: number) => {
    if (testResult) {
      return;
    }

    setSelectedAnswers((prev) => {
      const currentAnswers = prev[questionIndex] || [];
      const alreadySelected = currentAnswers.includes(optionIndex);

      return {
        ...prev,
        [questionIndex]: alreadySelected
          ? currentAnswers.filter((answer) => answer !== optionIndex)
          : [...currentAnswers, optionIndex],
      };
    });
  };

  const getCorrectAnswerIndexes = (question: TestQuestionItem): number[] => {
    const rawAnswers =
      question.correctAnswers ||
      question.correct_answers ||
      question.correctAnswerIndexes ||
      question.correct_answer_indexes ||
      question.correctOptionIndexes ||
      question.correct_option_indexes ||
      [];

    return rawAnswers
      .map((answer) => Number(answer))
      .filter((answer) => !Number.isNaN(answer));
  };

  const getQuestionScore = (question: TestQuestionItem, questionIndex: number) => {
    const selected = selectedAnswers[questionIndex] || [];
    const correctAnswers = getCorrectAnswerIndexes(question);
    const marks = Number(question.marks || 0);

    if (correctAnswers.length === 0) {
      return 0;
    }

    const pointPerCorrectAnswer = marks / correctAnswers.length;

    let score = 0;

    selected.forEach((selectedOption) => {
      if (correctAnswers.includes(selectedOption)) {
        score += pointPerCorrectAnswer;
      }
    });

    return Number(score.toFixed(2));
  };

  const isQuestionFullyCorrect = (question: TestQuestionItem, questionIndex: number) => {
    const selected = selectedAnswers[questionIndex] || [];
    const correctAnswers = getCorrectAnswerIndexes(question);

    return (
      selected.length === correctAnswers.length &&
      selected.every((answer) => correctAnswers.includes(answer))
    );
  };


  const getLoggedInUser = (): LoggedInUser | null => {
  const storedUser = sessionStorage.getItem("loggedInUser");

  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser);
  } catch {
    return null;
  }
};

  const handleSubmitTest = async () => {
  if (!selectedTest || testResult) {
    return;
  }

  const loggedInUser = getLoggedInUser();

  if (!loggedInUser?.id) {
    alert("ログインユーザー情報が見つかりません。再ログインしてください。");
    return;
  }

  let score = 0;

  selectedQuestions.forEach((question, questionIndex) => {
    score += getQuestionScore(question, questionIndex);
  });

  const finalScore = Number(score.toFixed(2));
  const passMark = Number(selectedTest.pass_mark || 0);

  const result = {
    score: finalScore,
    passed: finalScore >= passMark,
  };

  try {
    const response = await fetch("/test-submissions/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        test_id: selectedTest.id,
        user_id: Number(loggedInUser.id),
        test_title: selectedTest.title,
        course_name: selectedTest.course_name,
        score: result.score,
        passed: result.passed ? "true" : "false",
        selected_answers_json: JSON.stringify(selectedAnswers),
        submitted_at: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);

      if (response.status === 400) {
        alert("このテストはすでに受験済みです。再受験はできません。");
      } else {
        alert(
  errorData?.detail
    ? JSON.stringify(errorData.detail)
    : "テスト結果の保存に失敗しました。"
);
      }

      return;
    }

    const stored = localStorage.getItem(COMPLETED_TESTS_KEY);
    let existingResults: CompletedTestResult[] = [];

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        existingResults = Array.isArray(parsed) ? parsed : [];
      } catch {
        existingResults = [];
      }
    }

    const selectedTestStorageKey = getTestStorageKey(selectedTest);

    const updatedResults = [
      ...existingResults.filter((item) => item.testId !== selectedTestStorageKey),
      {
        testId: selectedTestStorageKey,
        score: result.score,
        passed: result.passed,
        selectedAnswers,
      },
    ];

    localStorage.setItem(COMPLETED_TESTS_KEY, JSON.stringify(updatedResults));
    setCompletedRefreshKey((prev) => prev + 1);
    setTestResult(result);
  } catch (error) {
    console.error("Test submission error:", error);
    alert("テスト結果の保存中にエラーが発生しました。");
  }
};

  if (!selectedTest) {
    return (
      <div style={pageStyle}>
        {employeeTests.length === 0 && (
          <div style={emptyCardStyle}>現在、割り当てられているテストはありません。</div>
        )}

        {employeeTests.map((test) => {
          const completedResult = getCompletedResult(test);

          return (
            <div key={getTestStorageKey(test)} style={testListCardStyle}>
              <div style={iconBoxStyle}>📝</div>

              <div style={{ flex: 1 }}>
                <div style={titleStyle}>{test.title}</div>
                <div style={messageStyle}>
                  コース：{test.course_name} / 問題数：{test.question_count}問 / 合格点：
                  {test.pass_mark || 0}点
                </div>
                <div style={dateStyle}>期限：{test.end_date || "未設定"}</div>

                {completedResult && (
                  <div style={completedBadgeStyle}>
                    受験済み：{completedResult.passed ? "合格" : "不合格"} / 得点：
                    {completedResult.score}点
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => handleStartTest(test)}
                style={completedResult ? secondaryButtonStyle : primaryButtonStyle}
              >
                {completedResult ? "結果を確認する" : "テストを開始する"}
              </button>
            </div>
          );
        })}
      </div>
    );
  }


  

  return (
    <div style={pageStyle}>
      <button type="button" onClick={handleBackToList} style={backButtonStyle}>
        ← テスト一覧へ戻る
      </button>

      <div style={summaryCardStyle}>
        <h2 style={{ marginTop: 0 }}>{selectedTest.title}</h2>
        <div style={messageStyle}>コース：{selectedTest.course_name}</div>

        <div style={summaryGridStyle}>
          <div style={summaryItemStyle}>
            問題数：{selectedTest.question_count || selectedQuestions.length}問
          </div>
          <div style={summaryItemStyle}>総得点：{selectedTest.total_marks || 0}点</div>
          <div style={summaryItemStyle}>合格点：{selectedTest.pass_mark || 0}点</div>
        </div>
      </div>

      {selectedQuestions.length === 0 && (
        <div style={emptyCardStyle}>このテストには表示できる問題がありません。</div>
      )}

      {selectedQuestions.map((question, questionIndex) => {
        const correctAnswers = getCorrectAnswerIndexes(question);
        const selectedForQuestion = selectedAnswers[questionIndex] || [];
        const questionScore = getQuestionScore(question, questionIndex);
        const questionFullyCorrect = isQuestionFullyCorrect(question, questionIndex);

        return (
          <div key={questionIndex} style={questionCardStyle}>
            <div style={titleStyle}>問題 {questionIndex + 1}</div>

            <div style={messageStyle}>
              {question.questionText || question.question_text || "問題文がありません"}
            </div>

            {(question.options || []).map((option, optionIndex) => {
              const isSelected = selectedForQuestion.includes(optionIndex);
              const isCorrectAnswer = correctAnswers.includes(optionIndex);
              const isWrongSelected = !!testResult && isSelected && !isCorrectAnswer;

              return (
                <label
                  key={optionIndex}
                  style={{
                    ...optionLabelStyle,
                    ...(testResult && isCorrectAnswer ? correctOptionStyle : {}),
                    ...(isWrongSelected ? wrongOptionStyle : {}),
                  }}
                >
                  <input
                    type="checkbox"
                    disabled={!!testResult}
                    checked={isSelected}
                    onChange={() => handleToggleAnswer(questionIndex, optionIndex)}
                  />
                  <span>{option || `選択肢 ${optionIndex + 1}`}</span>

                  {testResult && isCorrectAnswer && (
                    <span style={correctBadgeStyle}>正解</span>
                  )}

                  {isWrongSelected && <span style={wrongBadgeStyle}>あなたの回答</span>}
                </label>
              );
            })}

            {testResult && (
              <div style={questionFullyCorrect ? questionCorrectStyle : questionWrongStyle}>
                {questionFullyCorrect
                  ? `この問題は正解です。獲得点：${questionScore}点`
                  : `この問題の獲得点：${questionScore}点 / 正解を確認してください。`}
              </div>
            )}
          </div>
        );
      })}

      {!testResult && (
        <button type="button" onClick={handleSubmitTest} style={submitButtonStyle}>
          回答を提出する
        </button>
      )}

      {testResult && (
        <div style={testResult.passed ? passedResultStyle : failedResultStyle}>
          <div style={{ fontSize: "22px", fontWeight: 900 }}>
            {testResult.passed ? "合格" : "不合格"}
          </div>
          <div style={{ marginTop: "8px" }}>
            あなたの得点：{testResult.score}点 / 合格点：{selectedTest.pass_mark || 0}点
          </div>
          <div style={{ marginTop: "8px" }}>
            このテストは受験済みです。再受験はできません。
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};

const testListCardStyle: React.CSSProperties = {
  backgroundColor: "#f0fdfa",
  border: "1px solid #99f6e4",
  borderRadius: "18px",
  padding: "22px",
  display: "flex",
  alignItems: "center",
  gap: "18px",
};

const iconBoxStyle: React.CSSProperties = {
  width: "46px",
  height: "46px",
  borderRadius: "14px",
  backgroundColor: "#ccfbf1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px",
};

const titleStyle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 900,
  color: "#0f172a",
  marginBottom: "8px",
};

const messageStyle: React.CSSProperties = {
  color: "#64748b",
  fontSize: "15px",
  marginBottom: "14px",
};

const dateStyle: React.CSSProperties = {
  marginTop: "16px",
  color: "#64748b",
  fontSize: "13px",
};

const primaryButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: "12px",
  fontWeight: 800,
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  backgroundColor: "#334155",
};

const backButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#ffffff",
  color: "#0f172a",
  padding: "12px 18px",
  borderRadius: "14px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
};

const summaryCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "40px",
};

const summaryGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "12px",
  marginTop: "18px",
};

const summaryItemStyle: React.CSSProperties = {
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "14px",
  padding: "14px",
  fontWeight: 800,
  color: "#0f172a",
};

const questionCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "24px",
};

const optionLabelStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "12px 14px",
  marginTop: "10px",
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: 700,
};

const correctOptionStyle: React.CSSProperties = {
  backgroundColor: "#ecfdf5",
  border: "1px solid #99f6e4",
};

const wrongOptionStyle: React.CSSProperties = {
  backgroundColor: "#fef2f2",
  border: "1px solid #fecaca",
};

const submitButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "14px 18px",
  borderRadius: "12px",
  fontWeight: 900,
  cursor: "pointer",
};

const emptyCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "40px",
  color: "#64748b",
  fontSize: "18px",
  fontWeight: 700,
};

const passedResultStyle: React.CSSProperties = {
  backgroundColor: "#ecfdf5",
  border: "1px solid #99f6e4",
  color: "#0f766e",
  borderRadius: "18px",
  padding: "24px",
  fontWeight: 800,
};

const failedResultStyle: React.CSSProperties = {
  backgroundColor: "#fef2f2",
  border: "1px solid #fecaca",
  color: "#dc2626",
  borderRadius: "18px",
  padding: "24px",
  fontWeight: 800,
};

const correctBadgeStyle: React.CSSProperties = {
  marginLeft: "auto",
  backgroundColor: "#bbf7d0",
  color: "#166534",
  padding: "5px 10px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 900,
};

const wrongBadgeStyle: React.CSSProperties = {
  marginLeft: "auto",
  backgroundColor: "#fecaca",
  color: "#991b1b",
  padding: "5px 10px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 900,
};

const questionCorrectStyle: React.CSSProperties = {
  marginTop: "14px",
  color: "#0f766e",
  fontWeight: 800,
};

const questionWrongStyle: React.CSSProperties = {
  marginTop: "14px",
  color: "#dc2626",
  fontWeight: 800,
};

const completedBadgeStyle: React.CSSProperties = {
  display: "inline-block",
  marginTop: "6px",
  backgroundColor: "#e0f2fe",
  color: "#0369a1",
  padding: "6px 12px",
  borderRadius: "999px",
  fontSize: "13px",
  fontWeight: 900,
};

export default EmployeeTestPage;